import type { FeatureLike } from 'ol/Feature.js';
import type { Geometry } from 'ol/geom';
import { Point } from 'ol/geom';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';

/// A shorthand to make it easier to understand. Even if they are identical, it makes sense to distinguish them.
type Icon = Style[];

/**
 * Props when generating a generic marker.
 */
interface GenericMarkerProps {
  icon: string;
  iconFillColor?: string;
  iconStrokeColor?: string;
  iconStrokeWidth?: number;
}

interface FeatureLabelProps {
  text: string;
  textColor?: string;
  offsetX?: number;
  offsetY?: number;
}

interface DynamicLabelCacheEntry {
  revision: number;
  name: string;
  paddingY: number;
  labelStyles: Style[];
}

/**
 * One-stop-shop for feature icon styles, labels for Features, etc.
 */
export class FeatureStyleManager {
  static LABEL_Y_DISTANCE_FROM_ICON = 22;

  // Per-feature cache for the dynamic label, invalidated when the geometry, name or padding change.
  private static readonly dynamicLabelCache = new WeakMap<FeatureLike, DynamicLabelCacheEntry>();

  static getPinFeatureStyle(text: string): Style[] {
    const markerIcon = this.genericMarkerIcon({ icon: 'place' });
    return this.addStaticLabelWithText(text, markerIcon);
  }

  /**
   * Constructs a generic marker icon.
   */
  static genericMarkerIcon({ icon, iconFillColor = '#FFFFFF', iconStrokeColor = '#000000', iconStrokeWidth = 0 }: GenericMarkerProps): Icon {
    return [
      new Style({
        zIndex: 1000,
        text: new Text({
          text: icon,
          font: '26px "grammar-icons"',
          fill: new Fill({ color: iconFillColor }),
          stroke:
            iconStrokeWidth > 0
              ? new Stroke({ color: iconStrokeColor, width: iconStrokeWidth })
              : undefined,
          textAlign: 'center',
          textBaseline: 'middle',
        }),
      }),
    ];
  }

  /**
   * Gives you styling for the default user tracking icon.
   */
  static defaultUserIcon(): Icon {
    const icon = 'person';
    const iconFillColor = '#FFFFFF';
    const iconStrokeColor = '#000000';
    const iconStrokeWidth = 5;
    return [
      new Style({
        zIndex: 1000,
        text: new Text({
          text: icon,
          font: '26px "grammar-icons"',
          fill: new Fill({ color: iconFillColor }),
          stroke:
            iconStrokeWidth > 0
              ? new Stroke({ color: iconStrokeColor, width: iconStrokeWidth })
              : undefined,
          textAlign: 'center',
          textBaseline: 'middle',
        }),
      }),
    ];
  }

  /**
   * Adds a static label with the given text, to the given style. This function is appropriate when labelling features with a static size (i.e. icons of a fixed size that can be known at compile-time). Will ensure smooth transitions of the label object when zooming. For dynamically-sized objects, use addDynamicLabel.
   * @param text
   * @param styles
   * @param textColor
   */
  static addStaticLabelWithText(text: string, styles: Style[], textColor?: string): Style[] {
    const labelStyles = this.addFeatureLabel({ text, textColor, offsetY: this.LABEL_Y_DISTANCE_FROM_ICON });

    return [...styles, ...labelStyles];
  }

  /**
   *  Adds a label below the feature if the feature has a 'name' property and 'nameVisible' is true.
   *  Returns the original styles plus the label style, with a paddingY pixel offset, if applicable.
   *  Must be used when the size of the parent feature that should be labelled, is dynamic, but produces visual artefacts when zooming. If the size of the feature to be labelled is static and known ahead of time, use addStaticLabel.
   * @param feature
   * @param styles
   * @param paddingY
   */
  static addDynamicLabelToStylesFromFeatureData(
    feature: FeatureLike,
    styles: Style[],
    paddingY = 12,
  ): Style[] {
    // NOTE: This is a monster of a function, with deep dependencies to the provided feature, and complicated logic to determine geometry from feature/styles. Let's get rid of it when we can.
    const nameValue: unknown = feature.get('name');
    const nameText = typeof nameValue === 'string' ? nameValue : '';
    const nameVisible = (feature.get('nameVisible') as boolean | undefined) ?? false;

    if (!nameVisible || nameText.length === 0) {
      return styles;
    }

    // This right here is troublesome
    const geometry = this.getGeometry(feature, styles);

    if (!geometry) {
      return styles;
    }

    const extent = geometry.getExtent();

    // This is where we calculate the correct label position, based on the extent of the feature.
    const labelStyles = this.getCachedOrBuildDynamicLabel(feature, geometry, nameText, paddingY, extent);

    return [...styles, ...labelStyles];
  }

  /**
   * The label styles for a feature, reused from the cache when nothing that affects them changed.
   */
  private static getCachedOrBuildDynamicLabel(
    feature: FeatureLike,
    geometry: Geometry,
    nameText: string,
    paddingY: number,
    extent: number[],
  ): Style[] {
    // Only cache when the label follows the feature's own geometry: style-derived geometries
    // (e.g. circle) are rebuilt each render and have no stable revision to key on.
    if (geometry !== feature.getGeometry()) {
      return this.buildDynamicLabelStyles(extent, nameText, paddingY);
    }

    const revision = geometry.getRevision();
    const cached = this.dynamicLabelCache.get(feature);

    if (cached && cached.revision === revision && cached.name === nameText && cached.paddingY === paddingY) {
      return cached.labelStyles;
    }

    const labelStyles = this.buildDynamicLabelStyles(extent, nameText, paddingY);

    this.dynamicLabelCache.set(feature, { revision, name: nameText, paddingY, labelStyles });

    return labelStyles;
  }

  private static buildDynamicLabelStyles(extent: number[], nameText: string, paddingY: number): Style[] {
    const labelGeometry = this.getDynamicLabelPosition(extent);

    return this.addFeatureLabel({ text: nameText, offsetY: paddingY }).map((style) => {
      style.setGeometry(labelGeometry);
      return style;
    });
  }

  private static addFeatureLabel({
    text,
    textColor = 'rgba(0, 0, 0, 0)',
    offsetX = 0,
    offsetY = 0,
  }: FeatureLabelProps): Style[] {
    const textConfig = {
      text,
      font: 'normal 400 16px/16px "Source Sans Pro"',
      offsetX,
      textAlign: 'center' as const,
      textBaseline: 'top' as const,
    };

    return [
      // Rounded corner background hack (https://github.com/openlayers/openlayers/issues/11013)
      new Style({
        zIndex: 1000,
        text: new Text({
          ...textConfig,
          offsetY,
          fill: new Fill({ color: textColor }),
          backgroundStroke: new Stroke({
            color: 'rgba(15, 28, 36, 1)',
            width: 6,
            lineCap: 'round',
            lineJoin: 'round',
          }),
        }),
      }),
      // Main text layer
      new Style({
        zIndex: 1000,
        text: new Text({
          ...textConfig,
          offsetY: offsetY + 1,
          fill: new Fill({ color: 'rgba(255, 255, 255, 1)' }),
          backgroundFill: new Fill({ color: 'rgba(15, 28, 36, 1)' }),
        }),
      }),
    ];
  }

  private static getDynamicLabelPosition(extent: number[]): Point {
    const [minX, minY, maxX] = extent;
    if (minX === undefined || minY === undefined || maxX === undefined) {
      return new Point([0, 0]);
    }
    const centerX = (minX + maxX) / 2;
    const bottomY = minY;
    return new Point([centerX, bottomY]);
  }

  /**
   * Gets the geometry of either the Feature or the styles.
   * @param feature
   * @param styles
   * @private
   */
  private static getGeometry(feature: FeatureLike, styles: Style[]): Geometry | null {
    // Check the styles geometry as some features (circle) have custom geometries defined in their styles
    const styleGeometry = styles.find(style => style.getGeometry() !== undefined)?.getGeometry();
    const geometry = typeof styleGeometry === 'function' ? feature.getGeometry() : styleGeometry ?? feature.getGeometry();

    if (geometry === undefined || typeof geometry === 'string' || typeof geometry === 'function') {
      return null;
    }

    return geometry as Geometry;
  }
}
